import { useMemo, useState } from 'react';
import type { RoleMapCoverage } from '@/api/console';
import type { RoleCoverageData } from './useRoleCoverageData';
import { buildRoleKeyByKey } from './coverageHelpers';
import {
    buildMatrixColumns,
    buildMatrixRows,
    computeMatrixTallies,
    type MatrixAppColumn,
    type MatrixRoleRow,
    type MatrixTallies,
} from './coverageMatrixHelpers';

/**
 * Frame 55a's three mutually-exclusive filter chips. `uncovered` and `paused`
 * keep a ROW when it has at least one such cell — the chips read "Chỉ ô chưa
 * phủ" / "Cần xử lý", and a matrix filters by row because a cell cannot be
 * hidden without leaving a hole in its row.
 */
export type MatrixFilter = 'all' | 'uncovered' | 'paused';

/**
 * EAA-1817: the **Ma trận** view's own state and derivations (design frame
 * 55a) — standard roles down, applications across.
 *
 * Kept apart from `useCoverageBoard` because the two views share their DATA
 * (one `GET /role-map/coverage`, one `GET /role-keys`, one `GET /admin/apps`,
 * all four already owned by `useRoleCoverageData` and cached under the same
 * query keys) but nothing else: the board's state is "which app is open in the
 * editor", and this view's is "which slice of the grid is on screen". Merging
 * them would put two unrelated filter models in one hook.
 *
 * SEARCH FILTERS BOTH AXES, and that needs saying because it is the one piece
 * of behaviour the frame's placeholder ("Tìm vai trò hoặc ứng dụng…") implies
 * but does not draw. The term narrows whichever axis it matches: rows by role
 * name/key, columns by app name/code, independently. A term matching only apps
 * keeps every row (they are all still relevant to the surviving columns) and a
 * term matching only roles keeps every column. A term matching neither empties
 * the grid — that is the honest answer, not a bug.
 */
export function useCoverageMatrix(data: RoleCoverageData) {
    const { coverageQuery, adminAppsQuery, roleKeysQuery } = data;

    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<MatrixFilter>('all');
    const [hideNotReady, setHideNotReady] = useState(false);

    const coverage: RoleMapCoverage | undefined = coverageQuery.data;
    const apps = useMemo(() => adminAppsQuery.data ?? [], [adminAppsQuery.data]);
    const roleKeyByKey = useMemo(
        () => buildRoleKeyByKey(roleKeysQuery.data ?? []),
        [roleKeysQuery.data]
    );

    const allColumns = useMemo<MatrixAppColumn[]>(
        () => (coverage ? buildMatrixColumns(apps, coverage) : []),
        [apps, coverage]
    );

    const allRows = useMemo<MatrixRoleRow[]>(
        () => (coverage ? buildMatrixRows(allColumns, coverage, roleKeyByKey) : []),
        [allColumns, coverage, roleKeyByKey]
    );

    /** Tallies describe the WHOLE grid, never the filtered slice — the chips
     *  they label are what does the filtering, so a count that moved with the
     *  filter would change the moment it was used. */
    const tallies = useMemo<MatrixTallies>(
        () => computeMatrixTallies(allColumns, allRows),
        [allColumns, allRows]
    );

    const notReadyCount = useMemo(
        () => allColumns.filter((column) => !column.ready).length,
        [allColumns]
    );

    const term = search.trim().toLowerCase();

    /** Does the term match at least one column? Decides whether the term is
     *  allowed to narrow the column axis at all (see the hook docstring). */
    const termMatchesAnyColumn = useMemo(() => {
        if (!term) return false;
        return allColumns.some((column) =>
            `${column.code} ${column.name}`.toLowerCase().includes(term)
        );
    }, [allColumns, term]);

    const termMatchesAnyRow = useMemo(() => {
        if (!term) return false;
        return allRows.some((row) => `${row.roleKey} ${row.roleName}`.toLowerCase().includes(term));
    }, [allRows, term]);

    const visibleColumns = useMemo(() => {
        let out = allColumns;
        if (hideNotReady) out = out.filter((column) => column.ready);
        if (term && termMatchesAnyColumn) {
            out = out.filter((column) =>
                `${column.code} ${column.name}`.toLowerCase().includes(term)
            );
        }
        return out;
    }, [allColumns, hideNotReady, term, termMatchesAnyColumn]);

    const visibleRows = useMemo(() => {
        let out = allRows;
        if (filter === 'uncovered') out = out.filter((row) => row.hasUncovered);
        else if (filter === 'paused') out = out.filter((row) => row.hasPaused);
        if (term && termMatchesAnyRow) {
            out = out.filter((row) =>
                `${row.roleKey} ${row.roleName}`.toLowerCase().includes(term)
            );
        } else if (term && !termMatchesAnyColumn) {
            // The term matches nothing on either axis — an empty grid, not a
            // silently unfiltered one.
            out = [];
        }
        return out;
    }, [allRows, filter, term, termMatchesAnyRow, termMatchesAnyColumn]);

    /** Column-key lookup so a cell renderer can ask "is this column ready?"
     *  without re-scanning the array per cell. */
    const columnByCode = useMemo(
        () => new Map(allColumns.map((column) => [column.code, column])),
        [allColumns]
    );

    const rowByKey = useMemo(
        () => new Map(allRows.map((row) => [row.roleKey, row])),
        [allRows]
    );

    function clearFilters() {
        setSearch('');
        setFilter('all');
        setHideNotReady(false);
    }

    const filtersActive = term.length > 0 || filter !== 'all' || hideNotReady;

    return {
        search,
        setSearch,
        filter,
        setFilter,
        hideNotReady,
        setHideNotReady,
        clearFilters,
        filtersActive,
        allColumns,
        allRows,
        visibleColumns,
        visibleRows,
        columnByCode,
        rowByKey,
        tallies,
        notReadyCount,
    };
}

export type CoverageMatrixVM = ReturnType<typeof useCoverageMatrix>;
